// Eskom PARCC — status → icon/tone/label classification (owner R3 feedback: show
// statuses as icons + a legend, not long text pills).
//
// Pure + React-free so it unit-tests in isolation; the StatusIcon component maps
// the returned `iconKey` to a lucide glyph and `tone` to a colour token. The
// compliance vocabulary is large, overlapping and still evolving (COMPLIANCE_LEVELS,
// NEW_COMPLIANCE_LEVELS, CES_STATUS_OPTIONS in domain/enums.ts), so we classify by
// an ORDERED keyword rule list (first match wins) rather than an exhaustive exact
// map — robust to wording drift and to levels that contain several keywords (e.g.
// "Non-Compliance: Possible IE" must resolve to the irregular-expenditure bucket,
// not the generic non-compliance one, so that rule is checked first).

export type Tone = 'ok' | 'bad' | 'warn' | 'house' | 'na' | 'neutral';

/** Stable glyph keys; mapped to lucide icons in StatusIcon.tsx. */
export type IconKey =
  | 'ok'
  | 'ie'
  | 'policy'
  | 'housekeeping'
  | 'na'
  | 'pending'
  | 'reviewed'
  | 'notReviewed'
  | 'progress'
  | 'open'
  | 'closed'
  | 'deleted'
  | 'unknown';

export interface StatusMeta {
  tone: Tone;
  iconKey: IconKey;
  /** Human label (the original value, trimmed) for tooltip + legend + a11y. */
  label: string;
  /** True when the level is a self-declared (entity-reported) variant. */
  selfDeclared: boolean;
}

interface Rule {
  test: RegExp;
  tone: Tone;
  iconKey: IconKey;
}

// First match wins — order encodes precedence.
const RULES: Rule[] = [
  // Four-state gap/finding lifecycle (9 Jun call) — exact-ish matches first so
  // "Closed by Self-Declaration" never falls into the compliance buckets.
  { test: /^closed( by self[\s-]?declaration)?$/i, tone: 'ok', iconKey: 'closed' },
  { test: /^open$/i, tone: 'warn', iconKey: 'open' },
  // Irregular expenditure / PFMA — the most serious; checked before generic non-compliance.
  { test: /possible ie|irregular|pfma|possible non-compliance/i, tone: 'bad', iconKey: 'ie' },
  // Housekeeping — checked before the broad non-compliance rule (it contains "non-compliance").
  { test: /housekeeping/i, tone: 'house', iconKey: 'housekeeping' },
  // Administrative control weakness.
  { test: /administrative control/i, tone: 'house', iconKey: 'housekeeping' },
  // Policy & procedures / internal policies.
  { test: /policy|procedure|internal polic/i, tone: 'warn', iconKey: 'policy' },
  // Not applicable.
  { test: /not[\s-]?applicable/i, tone: 'na', iconKey: 'na' },
  // Generic non-conformance / non-compliance (after the specific buckets above).
  { test: /non[\s-]?conformance|non[\s-]?complian/i, tone: 'warn', iconKey: 'policy' },
  // Deleted / to-delete.
  { test: /to delete|deleted/i, tone: 'na', iconKey: 'deleted' },
  // In progress.
  { test: /in progress/i, tone: 'neutral', iconKey: 'progress' },
  // Pending review.
  { test: /pending/i, tone: 'neutral', iconKey: 'pending' },
  // Not reviewed.
  { test: /not reviewed/i, tone: 'neutral', iconKey: 'notReviewed' },
  // Reviewed (review-status) — checked late so "Pending Review" doesn't match here.
  { test: /reviewed|^reviewed$/i, tone: 'ok', iconKey: 'reviewed' },
  // Compliant (after non-compliant has been ruled out above).
  { test: /complian/i, tone: 'ok', iconKey: 'ok' },
];

const NEUTRAL: StatusMeta = { tone: 'neutral', iconKey: 'unknown', label: '—', selfDeclared: false };

/** Classify a compliance / CES / review status string into tone + icon + label. */
export function statusMeta(value: string | null | undefined): StatusMeta {
  if (typeof value !== 'string') return NEUTRAL;
  const label = value.trim();
  if (label === '') return NEUTRAL;
  // Matches "Self-Declared: …" levels AND "Closed by Self-Declaration".
  const selfDeclared = /self[\s-]?declar/i.test(label);
  for (const rule of RULES) {
    if (rule.test.test(label)) {
      return { tone: rule.tone, iconKey: rule.iconKey, label, selfDeclared };
    }
  }
  return { tone: 'neutral', iconKey: 'unknown', label, selfDeclared };
}

/** Meta for the boolean "reviewed?" flag shown on transaction rows. */
export function reviewedMeta(reviewed: boolean): StatusMeta {
  return reviewed
    ? { tone: 'ok', iconKey: 'reviewed', label: 'Reviewed', selfDeclared: false }
    : { tone: 'neutral', iconKey: 'notReviewed', label: 'Not reviewed', selfDeclared: false };
}

/** Canonical buckets for the on-screen legend (one row per distinct meaning). */
export interface LegendItem {
  iconKey: IconKey;
  tone: Tone;
  label: string;
  hint: string;
  /** True → rendered ringed (the self-declared variant of the category). */
  selfDeclared?: boolean;
}

// Associations corrected per the NTCSA legend / 9 Jun alignment call: Possible
// IE is the PFMA-anchored most-severe bucket (incl. "Possible Non-Compliance");
// bare "Non-Conformance" belongs to policy & procedures; administrative control
// weakness belongs to housekeeping. Self-declared variants are listed
// explicitly per category (ringed), not just as a footnote.
export const COMPLIANCE_LEGEND: readonly LegendItem[] = [
  { iconKey: 'ok', tone: 'ok', label: 'Compliant', hint: 'Meets the requirement' },
  {
    iconKey: 'ie',
    tone: 'bad',
    label: 'Possible irregular expenditure (IE)',
    hint: 'PFMA-anchored: Non-Compliance: Possible IE / Possible Non-Compliance',
  },
  {
    iconKey: 'policy',
    tone: 'warn',
    label: 'Non-conformance: policy & procedures',
    hint: 'Internal policies, procedures & instructions (incl. Non-Conformance)',
  },
  {
    iconKey: 'housekeeping',
    tone: 'house',
    label: 'Housekeeping',
    hint: 'Housekeeping / administrative control weakness',
  },
  {
    iconKey: 'ie',
    tone: 'bad',
    label: 'Self-declared: possible IE',
    hint: 'Buyer-acknowledged PFMA-level gap (self-declaration)',
    selfDeclared: true,
  },
  {
    iconKey: 'policy',
    tone: 'warn',
    label: 'Self-declared: policy & procedures',
    hint: 'Buyer-acknowledged policy/procedure gap (self-declaration)',
    selfDeclared: true,
  },
  {
    iconKey: 'housekeeping',
    tone: 'house',
    label: 'Self-declared: housekeeping',
    hint: 'Buyer-acknowledged housekeeping gap (self-declaration)',
    selfDeclared: true,
  },
  { iconKey: 'na', tone: 'na', label: 'Not applicable', hint: 'Criterion does not apply' },
  { iconKey: 'pending', tone: 'neutral', label: 'Pending / not reviewed', hint: 'Awaiting assessment' },
];

// The four-state gap/finding lifecycle (9 Jun call): Open (not actioned),
// In progress (pending), Closed, Closed by self-declaration (buyer acknowledged
// the finding but full compliance was not achievable).
export const GAP_STATUS_LEGEND: readonly LegendItem[] = [
  { iconKey: 'open', tone: 'warn', label: 'Open', hint: 'Finding recorded, not yet actioned' },
  { iconKey: 'progress', tone: 'neutral', label: 'In progress', hint: 'Gap closure under way' },
  { iconKey: 'closed', tone: 'ok', label: 'Closed', hint: 'Gap resolved / compliant' },
  {
    iconKey: 'closed',
    tone: 'ok',
    label: 'Closed by self-declaration',
    hint: 'Buyer acknowledged the finding; full compliance not achievable',
    selfDeclared: true,
  },
];

export const REVIEW_LEGEND: readonly LegendItem[] = [
  { iconKey: 'reviewed', tone: 'ok', label: 'Reviewed', hint: 'Review complete' },
  { iconKey: 'progress', tone: 'neutral', label: 'In progress', hint: 'Review under way' },
  { iconKey: 'pending', tone: 'neutral', label: 'Pending review', hint: 'Submitted, awaiting review' },
  { iconKey: 'notReviewed', tone: 'neutral', label: 'Not reviewed', hint: 'Not yet started' },
];
